import React, { createContext, useState, useContext, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useRestaurantContext } from './RestaurantContext';
import { v4 as uuidv4 } from 'uuid';
import { createApiProvider } from '../config/apiConfig';
import MoneyUtils from '../utils/moneyUtils';

const CartContext = createContext();

export const useCartContext = () => useContext(CartContext);

const getAddonId = (a) => a.id || a._id || a.addonId || '';
const getPortionId = (p) => p.id || p._id || p.portionId || '';

const areCustomizationsEqual = (itemA, itemB) => {
  const custA = itemA.customizations;
  const custB = itemB.customizations;

  if (!custA && !custB) return true;
  if (!custA || !custB) return false;

  // 1. Portion comparison
  const portionA = custA.portion;
  const portionB = custB.portion;
  if (portionA || portionB) {
    if (!portionA || !portionB) return false;
    if (getPortionId(portionA) !== getPortionId(portionB)) return false;
  }

  // 2. Addons comparison
  const addonsA = (custA.addons || []).filter(a => a.quantity > 0);
  const addonsB = (custB.addons || []).filter(a => a.quantity > 0);
  if (addonsA.length !== addonsB.length) return false;

  const sortedA = [...addonsA].sort((x, y) => getAddonId(x).localeCompare(getAddonId(y)));
  const sortedB = [...addonsB].sort((x, y) => getAddonId(x).localeCompare(getAddonId(y)));

  for (let i = 0; i < sortedA.length; i++) {
    if (getAddonId(sortedA[i]) !== getAddonId(sortedB[i]) || sortedA[i].quantity !== sortedB[i].quantity) {
      return false;
    }
  }

  // 3. Combo Components comparison
  const comboA = custA.comboComponents || [];
  const comboB = custB.comboComponents || [];
  if (comboA.length !== comboB.length) return false;

  const sortedComboA = [...comboA].sort((x, y) => (x.id || x._id || '').localeCompare(y.id || y._id || ''));
  const sortedComboB = [...comboB].sort((x, y) => (x.id || x._id || '').localeCompare(y.id || y._id || ''));

  for (let i = 0; i < sortedComboA.length; i++) {
    const componentA = sortedComboA[i];
    const componentB = sortedComboB[i];
    if ((componentA.id || componentA._id) !== (componentB.id || componentB._id)) return false;
    
    const selA = componentA.selectedItem || {};
    const selB = componentB.selectedItem || {};
    if ((selA.id || selA._id) !== (selB.id || selB._id)) return false;
    if (selA.selectedOptionName !== selB.selectedOptionName) return false;
  }

  // 4. Special Instructions
  if ((itemA.specialInstructions || '') !== (itemB.specialInstructions || '')) return false;

  return true;
};

export const CartProvider = ({ children }) => {
  const { currentUser, openAuthModal, isAuthLoading } = useAuth();
  const { details, restaurantId } = useRestaurantContext();
  const [cartData, setCartData] = useState({
    items: [],
    subtotal: 0,
    tax: 0,
    deliveryFee: 0,
    discount: 0,
    total: 0,
    itemCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [menuItemModal, setMenuItemModal] = useState({
    isOpen: false,
    item: null,
  });
  const [isQueueMode, setIsQueueMode] = useState(false);
  const [queueGuestInfo, setQueueGuestInfo] = useState(null);

  const openMenuItemModal = (item) => {
    setMenuItemModal({ isOpen: true, item });
  };

  const closeMenuItemModal = () => {
    setMenuItemModal({ isOpen: false, item: null });
  };

  const api = useMemo(() => createApiProvider(), []);

  const calculateTotals = useCallback((items = [], discount = 0) => {
    const taxRules = details?.taxRules || [];
    const deliveryFee = 0; //todo adjust delivery fee as needed

    // item.price = base price only (portion / combo-adjusted, NO addons baked in).
    // Add-ons are stored separately in item.customizations.addons and must be
    // summed here so the displayed subtotal stays accurate.
    const subtotal = items.reduce((sum, item) => {
      const addonCost = (item.customizations?.addons || []).reduce(
        (aSum, addon) => aSum + (addon.price || 0) * (addon.quantity || 1),
        0
      );
      return sum + ((item.price || 0) + addonCost) * (item.quantity || 0);
    }, 0);

    let totalTax = 0;

    // Apply global and item-specific taxes accurately
    const globalRules = taxRules.filter(r => r.autoApply && !r.isItemSpecific);

    items.forEach(item => {
      // Include addon cost in the taxable base — mirrors billing counter behaviour.
      const addonCost = (item.customizations?.addons || []).reduce(
        (aSum, addon) => aSum + (addon.price || 0) * (addon.quantity || 1),
        0
      );
      const itemSubtotal = ((item.price || 0) + addonCost) * (item.quantity || 0);

      if (item.taxRuleId === 'NONE') {
        // Exempt
      } else if (item.taxRuleId) {
        const specificRule = taxRules.find(r => r.id === item.taxRuleId || r._id === item.taxRuleId);
        if (specificRule) {
          if (specificRule.calculationMethod === 'percentageOnSubtotal') {
            totalTax += itemSubtotal * (specificRule.rate / 100);
          } else if (specificRule.calculationMethod === 'fixedAmount') {
            totalTax += specificRule.rate * (item.quantity || 1);
          }
        } else {
          // Rule not found, fallback to global
          globalRules.forEach(rule => {
            if (rule.calculationMethod === 'percentageOnSubtotal') {
              totalTax += itemSubtotal * (rule.rate / 100);
            }
          });
        }
      } else {
        // Fallback to global
        globalRules.forEach(rule => {
          if (rule.calculationMethod === 'percentageOnSubtotal') {
            totalTax += itemSubtotal * (rule.rate / 100);
          }
        });
      }
    });

    // Apply global flat taxes ONCE
    if (items.length > 0) {
      globalRules.forEach(rule => {
        if (rule.calculationMethod === 'fixedAmount') {
          totalTax += rule.rate;
        }
      });
    }

    const tax = totalTax;
    const fee = items.length > 0 ? deliveryFee : 0;
    const total = subtotal + tax + fee - discount;
    const itemCount = items.reduce(
      (sum, item) => sum + (item.quantity || 0),
      0
    );
    return {
      subtotal: Number(subtotal.toFixed(2)),
      tax: Number(tax.toFixed(2)),
      deliveryFee: Number(fee.toFixed(2)),
      discount: Number(discount.toFixed(2)),
      total: Number(Math.max(0, total).toFixed(2)),
      itemCount,
    };
  }, [details?.taxRules]);

  const validateItem = (item) => {
    if (!item || !item?.name || !item?.price)
      return { isValid: false, error: "Invalid item data" };
    return { isValid: true };
  };

  const isValidQuantity = (quantity) => {
    return (
      typeof quantity === "number" && quantity > 0 && Number.isInteger(quantity)
    );
  };

  const withAuth = useCallback(
    (action) => {
      return (...args) => {
        if (!currentUser) {
          openAuthModal();
          return Promise.reject("User not authenticated");
        }
        return action(...args);
      };
    },
    [currentUser, openAuthModal]
  );

  const getCartKey = useCallback((uid, restoId) => {
    if (!restoId) return null;
    return uid ? `cart_${restoId}_${uid}` : `cart_${restoId}_guest`;
  }, []);

  const getCartFromLocalStorage = useCallback((uid, restoId) => {
    const cartKey = getCartKey(uid, restoId);
    if (!cartKey) return [];
    try {
      const storedCart = localStorage.getItem(cartKey);
      return storedCart ? JSON.parse(storedCart) : [];
    } catch (e) {
      console.error("Failed to read cart from local storage", e);
      return [];
    }
  }, [getCartKey]);

  const saveCartToLocalStorage = useCallback((uid, restoId, cartItems) => {
    const cartKey = getCartKey(uid, restoId);
    if (!cartKey) return;
    try {
      localStorage.setItem(cartKey, JSON.stringify(cartItems));
    } catch (e) {
      console.error("Failed to save cart to local storage", e);
    }
  }, [getCartKey]);

  const clearCartFromLocalStorage = useCallback((uid, restoId) => {
    const cartKey = getCartKey(uid, restoId);
    if (!cartKey) return;
    try {
      localStorage.removeItem(cartKey);
    } catch (e) {
      console.error("Failed to clear cart from local storage", e);
    }
  }, [getCartKey]);

  const fetchCartData = useCallback(async () => {
    if (isAuthLoading || !restaurantId) return;
    setLoading(true);
    setError(null);
    try {
      const items = getCartFromLocalStorage(currentUser?.uid, restaurantId);
      const updatedCalculations = calculateTotals(
        items,
        appliedPromo?.discountAmount || 0
      );
      setCartData({ items, ...updatedCalculations });
    } catch (err) {
      setError(
        "An unexpected error occurred while fetching cart from local storage"
      );
    } finally {
      setLoading(false);
    }
  }, [currentUser, isAuthLoading, restaurantId, getCartFromLocalStorage, appliedPromo, calculateTotals]);

  useEffect(() => {
    if (isAuthLoading || !restaurantId) return;

    if (currentUser) {
      const guestCart = getCartFromLocalStorage(null, restaurantId);
      if (guestCart.length > 0) {
        const userCart = getCartFromLocalStorage(currentUser.uid, restaurantId);
        const mergedCart = [...userCart, ...guestCart];
        saveCartToLocalStorage(currentUser.uid, restaurantId, mergedCart);
        clearCartFromLocalStorage(null, restaurantId);
      }
      fetchCartData();
    } else {
      fetchCartData();
    }
  }, [isAuthLoading, currentUser, restaurantId, fetchCartData, getCartFromLocalStorage, saveCartToLocalStorage, clearCartFromLocalStorage]);


  const addItem = useCallback(async (item, customizationDetails) => {
    const validationResult = validateItem(item);
    if (!validationResult?.isValid) {
      return { success: false, error: validationResult?.error };
    }

    const currentItems = getCartFromLocalStorage(currentUser?.uid, restaurantId);
    let newItems;

    if (customizationDetails) {
      const { quantity, unitBasePrice, ...otherDetails } = customizationDetails;
      // Store the base/portion price ONLY (addons are kept separately in
      // customizations.addons). This matches the billing counter's formula:
      // itemTotal = (item.price + addonTotal) * quantity
      // Using unitBasePrice passed from MenuItemModal; fall back to item.price
      // if not provided (e.g. simple non-customised items).
      const newItem = {
        ...item,
        price: unitBasePrice ?? item.price,
        quantity: quantity,
        cartItemId: uuidv4(),
        customizations: otherDetails.customizations,
        specialInstructions: otherDetails.specialInstructions,
      };

      const existingItemIndex = currentItems.findIndex(
        (cartItem) => cartItem.id === item.id && areCustomizationsEqual(cartItem, newItem)
      );

      if (existingItemIndex > -1) {
        newItems = [...currentItems];
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + quantity,
        };
      } else {
        newItems = [...currentItems, newItem];
      }
    } else {
      const existingItemIndex = currentItems.findIndex(
        (cartItem) => cartItem.id === item.id && !cartItem.customizations
      );
      if (existingItemIndex > -1) {
        newItems = [...currentItems];
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + 1,
        };
      } else {
        newItems = [
          ...currentItems,
          { ...item, quantity: 1, cartItemId: uuidv4() },
        ];
      }
    }

    saveCartToLocalStorage(currentUser?.uid, restaurantId, newItems);
    await fetchCartData();
    return { success: true, message: "Item added to cart successfully" };
  }, [currentUser?.uid, restaurantId, getCartFromLocalStorage, saveCartToLocalStorage, fetchCartData]);

  const updateQuantity = useCallback(async (cartItemId, quantity) => {
    if (!isValidQuantity(quantity)) {
      return { success: false, error: "Quantity must be a positive number" };
    }
    const currentItems = getCartFromLocalStorage(currentUser?.uid, restaurantId);
    const existingItemIndex = currentItems.findIndex(
      (cartItem) => cartItem.cartItemId === cartItemId
    );

    if (existingItemIndex > -1) {
      const newItems = [...currentItems];
      newItems[existingItemIndex] = {
        ...newItems[existingItemIndex],
        quantity: quantity,
      };
      saveCartToLocalStorage(currentUser?.uid, restaurantId, newItems);
      await fetchCartData();
      return { success: true, message: "Quantity updated" };
    }
    return { success: false, error: "Item not found in cart" };
  }, [currentUser?.uid, restaurantId, getCartFromLocalStorage, saveCartToLocalStorage, fetchCartData]);

  const removeItem = useCallback(async (cartItemId) => {
    const currentItems = getCartFromLocalStorage(currentUser?.uid, restaurantId);
    const newItems = currentItems.filter(
      (cartItem) => cartItem.cartItemId !== cartItemId
    );
    saveCartToLocalStorage(currentUser?.uid, restaurantId, newItems);
    await fetchCartData();
    return { success: true, message: "Item removed" };
  }, [currentUser?.uid, restaurantId, getCartFromLocalStorage, saveCartToLocalStorage, fetchCartData]);

  const clearCart = useCallback(async () => {
    clearCartFromLocalStorage(currentUser?.uid, restaurantId);
    clearCartFromLocalStorage(null, restaurantId);
    setIsQueueMode(false);
    await fetchCartData();
    return { success: true, message: "Cart cleared" };
  }, [currentUser?.uid, restaurantId, clearCartFromLocalStorage, fetchCartData]);

  // Returns all cart entries for a given item id — used by ItemRepeatSheet
  const getItemsInCartById = useCallback((itemId) => {
    return (cartData.items || []).filter(
      (ci) => ci.id === itemId || ci._id === itemId
    );
  }, [cartData.items]);

  const applyPromoCode = withAuth(async (code) => {
    const result = await api.applyPromoCode(code, cartData.subtotal);
    if (result.success) {
      setAppliedPromo(result.data);
      await fetchCartData();
    }
    return result;
  });

  const removePromoCode = useCallback(async () => {
    setAppliedPromo(null);
    await fetchCartData();
  }, [fetchCartData]);

  const submitOrder = withAuth(async (orderData) => {
    // Send orderData as-is since all prices and totals are already in smallest units
    const result = await api.submitOrder(orderData);
    if (result.success) {
      clearCartFromLocalStorage(currentUser.uid, restaurantId);
      await fetchCartData();
    }
    return result;
  });

  const value = useMemo(
    () => ({
      cartData,
      loading,
      error,
      appliedPromo,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      applyPromoCode,
      removePromoCode,
      submitOrder,
      refetch: fetchCartData,
      isEmpty: (cartData.items?.length ?? 0) === 0,
      hasItems: (cartData.items?.length ?? 0) > 0,
      menuItemModal,
      openMenuItemModal,
      closeMenuItemModal,
      isQueueMode,
      setIsQueueMode,
      queueGuestInfo,
      setQueueGuestInfo,
      getItemsInCartById,
    }),
    [
      cartData,
      loading,
      error,
      appliedPromo,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      applyPromoCode,
      removePromoCode,
      submitOrder,
      fetchCartData,
      menuItemModal,
      isQueueMode,
      queueGuestInfo,
      getItemsInCartById,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};